"use client";

import { useCallback, useState } from "react";

import {
	type AddToCartActionRequest,
	type AddToCartActionStatus
} from "@/app/actions/cart";
import { useCartStore } from "@/app/store";
import { runAddToCart, type RunAddToCartArgs } from "./run-add-to-cart";

export interface UseAddToCartOptions {
	product?: RunAddToCartArgs["product"];
	source?: RunAddToCartArgs["source"];
}

export function useAddToCart(
	payload: AddToCartActionRequest,
	options: UseAddToCartOptions = {}
): {
	cartQuantity: number;
	handleClick: () => Promise<void>;
	loading: boolean;
	error: string | null;
	errorStatus: AddToCartActionStatus | null;
	submitted: boolean;
} {
	const cartState = useCartStore((state) => state.cartState);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [errorStatus, setErrorStatus] =
		useState<AddToCartActionStatus | null>(null);
	const [submitted, setSubmitted] = useState(false);

	const cartQuantity =
		cartState.status === "connected"
			? cartState.entries.reduce((acc, entry) => acc + entry.quantity, 0)
			: 0;

	const { product, source = "pdp" } = options;

	const handleClick = useCallback(async () => {
		setLoading(true);
		setError(null);
		setErrorStatus(null);

		try {
			const result = await runAddToCart({
				request: payload,
				source,
				product
			});

			if (result.status !== "success") {
				setErrorStatus(result.status);
				setError(result.message);
				return;
			}

			setSubmitted(true);
			setTimeout(() => setSubmitted(false), 1000);
		} finally {
			setLoading(false);
		}
	}, [payload, product, source]);

	return {
		cartQuantity,
		handleClick,
		loading,
		error,
		errorStatus,
		submitted
	};
}
